import { useMemo, useRef, useState } from "react";

export interface FilterProps<S = Record<string, unknown>> {
  defaultState?: Partial<S>;
}

const cleanState = <S = Record<string, unknown>>(obj: S) => {
  return Object.fromEntries(
    Object.entries(obj as unknown as object).filter(
      ([, value]) => value !== null && value !== undefined
    )
  ) as S;
};

const useFilter = <S>(props?: FilterProps<S>) => {
  const draftFilterStateRef = useRef<S>({
    ...(cleanState(props?.defaultState || {}) as S),
  });

  const [state, setState] = useState<S>({
    ...(cleanState(props?.defaultState || {}) as S),
  });

  const setDraftFilter =
    <K extends keyof S>(key: K) =>
    (value: unknown) => {
      draftFilterStateRef.current = {
        ...(draftFilterStateRef.current || {}),
        ...{ [key]: value },
      } as unknown as S;
    };

  const setDraftFilters = (states: Partial<S>) => {
    draftFilterStateRef.current = {
      ...(draftFilterStateRef.current || {}),
      ...states,
    } as unknown as S;
  };

  const setFilter = <K extends keyof S>(key: K, value: S[K]) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const setFilters = (states: Partial<S>) => {
    setState((prevState) => ({
      ...prevState,
      ...states,
    }));
  };

  const setDraftStatesToFilterStates = () => {
    setState(draftFilterStateRef.current as unknown as S);
  };

  const resetDraftFilters = () => {
    draftFilterStateRef.current = {} as S;
  };

  const resetFilters = () => {
    setState({} as S);
  };

  const stateClearify = useMemo(() => cleanState(state), [state]);

  return {
    state: stateClearify,
    setDraftFilter,
    setDraftFilters,
    resetDraftFilters,
    setFilter,
    setFilters,
    resetFilters,
    setDraftStatesToFilterStates,
  };
};

export default useFilter;
